import { UserPlusIcon } from "@heroicons/react/24/solid";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@moe/oss/ui/button";
import { Form } from "@moe/oss/ui/form";
import { del } from "@moe/priv/idb";
import { CharacterInsert } from "@moe/priv/model/character";
import { DialogConfig } from "@moe/priv/types/types";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { UnAuthedMessage } from "@web/components/UnAuthedMessage";
import { useCreateChat } from "@web/hooks/use-create-chat";
import { useAppContext } from "@web/route-services/root/AppContext";
import { AdvanceFields } from "@web/route-services/studio/AdvanceFields";
import { BaseStudioView } from "@web/route-services/studio/BaseStudioView";
import { DetailsFields } from "@web/route-services/studio/DetailsField";
import { ImageFields } from "@web/route-services/studio/ImageFields";
import { CreateData, createDefaults, createSchema } from "@web/route-services/studio/schema";
import { TokenSummary } from "@web/route-services/studio/TokenSummary";
import { createFormIDBKey, useRestoreCreateForm } from "@web/route-services/studio/use-restore-create-form";
import { VisibilityFields } from "@web/route-services/studio/VisibilityFields";
import { useState } from "react";
import { useForm, useFormContext } from "react-hook-form";
import { toast } from "sonner";

export const Route = createFileRoute("/studio/create")({
  component: Create
});

function Create() {
  const form = useForm<any>({
    resolver: zodResolver(createSchema) as any,
    defaultValues: createDefaults as any
  });

  useRestoreCreateForm(form as any);

  return (
    <BaseStudioView form={form}>
      <Content />
    </BaseStudioView>
  );
}

function Content() {
  const { createDialog, session } = useAppContext();
  const { access } = useAppContext();
  const createChat = useCreateChat();
  const [showUnAuthedMessage, setShowUnAuthedMessage] = useState(false);
  const form = useFormContext<CreateData>();

  const createMutation = useMutation({
    mutationFn: async (data: CreateData) => {
      const value: CharacterInsert = {
        avatar: data.avatar,
        banner: data.banner,
        name: data.name,
        description: data.description,
        scenario: data.scenario,
        greeting: data.greeting,
        definitions: data.definitions,
        messageExamples: JSON.stringify(data.messageExamples),
        alternativeGreetings: data.alternativeGreetings,
        title: data.title,
        tagline: data.tagline,
        isNSFW: data.isNSFW,
        isPrivate: data.isPrivate
      };
      return await access.character.create(value);
    },
    onMutate: () => {
      toast.info("Creating character...");
    },
    onSuccess: async (id) => {
      toast.success("Character created!");
      const char = await access.character.getByID(id);
      createChat(char);
      form.reset(createDefaults);
      // Clear the persited form data on successful submission
      del(createFormIDBKey);
    },
    onError: (error) => {
      toast.error(`Failed to create character: ${error.message}`);
    }
  });

  const handleClear = () => {
    const config: DialogConfig = {
      title: "Clear Form?",
      actionLabel: "Clear",
      description:
        "Are you sure you want to clear the form? All unsaved changes will be lost. This action cannot be undone.",
      onAction: () => {
        form.reset(createDefaults);
        del(createFormIDBKey);
      }
    };
    createDialog(config);
  };

  const onSubmit = (data: CreateData) => {
    // Don't submit if the user is not signed in, instead show them a prompt to sign up / sign in
    if (!session) {
      setShowUnAuthedMessage(true);
      return;
    }
    if (createMutation.isPending) return;
    createMutation.mutate(data);
  };

  return (
    <div className="border-line relative w-full rounded-2xl border md:w-[45rem] md:min-w-[45rem]">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="bg-container-primary space-y-8">
          {/* Banner picture */}
          <ImageFields />
          <div className="bg-container-primary px-6 pb-6 pt-6">
            <div className=" flex flex-col space-y-10">
              <DetailsFields />
              <AdvanceFields />
              <VisibilityFields />
              <TokenSummary />

              {showUnAuthedMessage && !session && (
                <UnAuthedMessage
                  className="text-center"
                  actionMessage="create your character! Don't worry, your character was saved locally."
                />
              )}
              <div className="flex justify-end space-x-1">
                <Button type="button" variant="clear" onClick={handleClear}>
                  Clear
                </Button>
                <Button
                  type="submit"
                  onClick={() => {
                    if (!form.getValues("avatar")) {
                      toast.error("A character avatar is required.");
                      return;
                    }
                  }}
                >
                  <UserPlusIcon className="" />
                  Create
                </Button>
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
